<script lang="ts" setup generic="T extends string | number">
import WbIcon from '../../../wb/Icon/WbIcon.vue';
import { TransmissionMode } from '@/types/types';

const props = defineProps<{
  name: string;
  label: string;
  options: any;
  selected: T | TransmissionMode | undefined | null;
  available: T[];
  getOptionLabel: (value: T) => string | number;
  onSelect: (value: T) => void;
  onTrack?: (key: string, vehicle: any) => void;
  trackPrefix?: string;
  vehicle: any;
}>();

const isAvailable = (option: T) => props.available.includes(option);
const isSelected = (option: T) => option === props.selected;

const handleSelect = (option: T, event: Event) => {
  if (!isAvailable(option)) {
    event.preventDefault();
    return;
  }

  props.onSelect(option);
  props.onTrack?.(
    `${props.trackPrefix ?? props.name}.${option}`,
    props.vehicle,
  );
};
</script>

<template>
  <fieldset class="owvans-tech-data-tag-group">
    <legend class="owvans-tech-data-tag-group__legend wb-text-s">
      {{ label }}
    </legend>
    <div class="owvans-tech-data-tag-group__options">
      <label
        v-for="(option, index) in options"
        :key="`${name}-${option}`"
        class="wbx-tag wb-text-s-strong wbx-tag--tertiary owvans-tech-data-tag-group__option"
        :class="{
          'owvans-tech-data-tag-group__option--default': !isSelected(option),
          'owvans-tech-data-tag-group__option--active': isSelected(option),
          'owvans-tech-data-tag-group__option--disabled': !isAvailable(option),
        }"
      >
        <input
          class="owvans-tech-data-tag-group__input"
          type="radio"
          :name="name"
          :value="String(option)"
          :tabindex="
            isSelected(option) || (!props.selected && index === 0) ? 0 : -1
          "
          :checked="isSelected(option) || (!props.selected && index === 0)"
          :disabled="!isAvailable(option)"
          @change="handleSelect(option, $event)"
        />
        <span class="wb-text wb-text-m-strong-static">
          {{ getOptionLabel(option) }}
        </span>
        <WbIcon
          :name="
            isSelected(option)
              ? 'bds/hotspot-active/24'
              : 'bds/hotspot-default/24'
          "
          size="M"
          flipRtl
        />
      </label>
    </div>
  </fieldset>
</template>

<style scoped lang="scss">
.owvans-tech-data-tag-group {
  display: flex;
  flex-direction: column;
  gap: var(--wb-spacing-static-tiny);
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;

  &__legend {
    color: var(--wb-color-border-stronger-default);
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: var(--wb-spacing-static-tiny);
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    gap: var(--wb-spacing-static-smallest);
    min-inline-size: 0;
  }

  &__option {
    display: flex;
    flex: 0 0 auto;
    position: relative;
    align-items: center;
    gap: var(--wb-interactive-tag-md-gap-right);
    border: none;
    border-radius: var(--wb-border-radius-round);
    padding: var(--wb-spacing-static-tiniest) var(--wb-spacing-static-smallest);
    cursor: pointer;
  }

  &__option--default {
    background-color: transparent;
    border: var(--wb-border-weight-main) solid
      var(--wb-color-border-stronger-default);

    &:hover {
      box-shadow: 0 0 0 var(--wb-border-weight-main)
        var(--wb-color-border-strong-default);
    }
  }

  &__option--active {
    &:hover {
      background-color: var(--wb-color-shape-weak-hover);
    }
  }

  &__option--disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
      box-shadow: none;
    }
  }

  &__input {
    position: absolute;
    inset: 0;
    opacity: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    cursor: inherit;
  }

  &__option:has(.owvans-tech-data-tag-group__input:focus) {
    outline: var(--wb-border-weight-main) solid
      var(--wb-color-border-brand-active);
    outline-offset: 2px;
    border-radius: var(--wb-border-radius-round);
  }
}
</style>
